/**
 * Centralized tier pricing resolution.
 *
 * Resolves the effective price from a tiered special pricing record
 * based on quantity. Used by both PricingSourcePicker and OrderDetailModal.
 */

export interface TierPricingInput {
  tier_price_1: number | null;
  tier_price_2: number | null;
  tier_price_3: number | null;
  tier_price_2_min: number | null;
  tier_price_3_min: number | null;
}

export interface TierResolutionResult {
  price: number | null;
  tier: 1 | 2 | 3 | null;
  label: string | null;
}

/**
 * Resolve the effective tier price for a given quantity.
 *
 * Tiers are evaluated from highest to lowest:
 * - tier_price_3 if qty >= tier_price_3_min
 * - tier_price_2 if qty >= tier_price_2_min
 * - tier_price_1 (base, no minimum)
 */
export function resolveTierPrice(
  input: TierPricingInput,
  quantity: number,
): TierResolutionResult {
  const qty = isNaN(quantity) ? 0 : quantity;

  if (
    input.tier_price_3 !== null &&
    input.tier_price_3_min !== null &&
    qty >= input.tier_price_3_min
  ) {
    return {
      price: input.tier_price_3,
      tier: 3,
      label: `T3 (${input.tier_price_3_min}+ units)`,
    };
  }

  if (
    input.tier_price_2 !== null &&
    input.tier_price_2_min !== null &&
    qty >= input.tier_price_2_min
  ) {
    return {
      price: input.tier_price_2,
      tier: 2,
      label: `T2 (${input.tier_price_2_min}+ units)`,
    };
  }

  if (input.tier_price_1 !== null) {
    return {
      price: input.tier_price_1,
      tier: 1,
      label: 'T1 (base)',
    };
  }

  return { price: null, tier: null, label: null };
}

/**
 * Render the whole tier ladder for display where no quantity is in context
 * (e.g. a customer's price list).
 *
 * Returns e.g. `$134.95 / $124.95 (36+) / $116.95 (72+)`, or null when the
 * row carries no tier prices at all. Quantity breakpoints are included
 * because the base tier alone is misleading — it is the MOST expensive rung.
 */
export function formatTierLadder(
  input: TierPricingInput,
  formatPrice: (value: number) => string,
): string | null {
  const rungs: string[] = [];

  // `== null` deliberately: a payload that omits a tier field entirely (an
  // older API build, a partial serialization) arrives as undefined, and a
  // strict null check would render it as "undefined+" instead of dropping it.
  if (input.tier_price_1 != null) {
    rungs.push(formatPrice(input.tier_price_1));
  }
  for (const [price, min] of [
    [input.tier_price_2, input.tier_price_2_min],
    [input.tier_price_3, input.tier_price_3_min],
  ] as const) {
    if (price == null) continue;
    rungs.push(
      min != null ? `${formatPrice(price)} (${min}+)` : formatPrice(price),
    );
  }

  return rungs.length > 0 ? rungs.join(' / ') : null;
}
